// AgentManagement.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from '@chakra-ui/react';
import { getAgents, createAgent, updateAgent, toggleLockAgent } from '../actions/agentActions';
import { getGames } from '../actions/gameActions';
import { getStokezes } from '../actions/stokezActions';
import CascadingDropdown from '../components/CascadingDropdown';
import GameSelection from './GameSelection';
import GenericTable from '../components/GenericTable';
import BalanceTransferModal from '../components/BalanceTransferModal';
import TableSearchBar from '../components/TableSearchBar';
import { Plus, ChevronRight, DollarSign, Loader2, X, Lock, Unlock, Edit } from 'lucide-react';
import { throwUnlessApiSuccess } from '../utils/apiResponse';
import { applyRecipientBalancePatch } from '../utils/applyRecipientBalancePatch';
import styles from '../styles/ManagementTable.module.css';
import { usePrivilege } from '../hooks/usePrivilege';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';
import { useAppToast } from '../hooks/useAppToast';
import { findById, filterGamesByAssignedIds, idStr } from '../utils/entityIds';
import { getLoggedInUserRole } from '../utils/auth';

const EMPTY_AGENT = {
  username: '',
  password: '',
  stokez_id: '',
  super_stokez_id: '',
  admin_id: '',
  company_id: '',
  gameIds: [],
  commission_perc: 5,
  target_perc: 15,
  adminTargetNTP: '',
};

const AgentManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();
  const { showSuccess, showWarning, showApiError } = useAppToast();
  const { can } = usePrivilege();
  const { filterColumns, canShowField } = useFieldPrivilege();
  const { agents, loading, error } = useSelector((state) => state.agent);
  const games = useSelector((state) => state.game.games);
  const stokezes = useSelector((state) => state.stokez.stokezes);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filterStokezId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const stokezParam = params.get('stokez') || null;
    if (!stokezParam) return null;
    const found = stokezes.find((s) => s.username === stokezParam);
    return found ? found._id : null;
  }, [location.search, stokezes]);

  const [searchQuery, setSearchQuery] = useState('');

  const filteredByStokez = useMemo(() => {
    if (!filterStokezId) return agents;
    return agents.filter((a) => a.stokez_id === filterStokezId);
  }, [agents, filterStokezId]);

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) return filteredByStokez;
    const q = searchQuery.trim().toLowerCase();
    return filteredByStokez.filter((a) => {
      const username = (a.username || '').toLowerCase();
      const stokez = (a.stokez_name || '').toLowerCase();
      const superStokez = (a.super_stokez_name || '').toLowerCase();
      const admin = (a.admin_name || '').toLowerCase();
      const company = (a.company_name || '').toLowerCase();
      const gameCodes = (a.game_codes || []).join(' ').toLowerCase();
      return [username, stokez, superStokez, admin, company, gameCodes].some((s) => s.includes(q));
    });
  }, [filteredByStokez, searchQuery]);
  const [cascadingDropdownValues, setCascadingDropdownValues] = useState({});
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState({});
  const [newAgent, setNewAgent] = useState({ ...EMPTY_AGENT });
  const [availableGames, setAvailableGames] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const isSuperAdmin = getLoggedInUserRole() === 'super_admin';

  const handleOpenTransferModal = (toRole, toId, recipient = {}) => {
    setTransferDetails({
      toRole,
      toId,
      recipientBalance: recipient.balance,
      recipientBalanceCheck: recipient.balance_check,
    });
    setIsTransferOpen(true);
  };

  const handleBalanceTransfer = async ({ fromRole, fromId, toRole, toId, amount }) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL_ADMIN}`, {
      apiName: 'user',
      command: 'transferBalance',
      fromRole,
      fromId,
      toRole,
      toId,
      amount,
    });
    return throwUnlessApiSuccess(response);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([dispatch(getAgents()), dispatch(getGames()), dispatch(getStokezes())]);
      } catch (err) {
        console.error('Error fetching data:', err);
        showApiError(err, 'Error fetching data');
      }
    };
    fetchData();
  }, [dispatch, showApiError]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewAgent((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleCascadingDropdownChange = useCallback((selections) => {
    setNewAgent((prev) => ({
      ...prev,
      ...selections,
      gameIds: [], // Reset gameIds when selections change
    }));
  }, []);

  useEffect(() => {
    if (!newAgent.stokez_id) {
      setAvailableGames([]);
      return;
    }
    const selectedStokez = findById(stokezes, newAgent.stokez_id);
    const filteredGames = filterGamesByAssignedIds(games, selectedStokez?.gameIds);
    setAvailableGames(
      filteredGames.length
        ? filteredGames
        : games.length && newAgent.stokez_id
          ? games
          : []
    );
  }, [newAgent.stokez_id, stokezes, games]);

  const handleGameSelection = useCallback((gameId) => {
    const id = idStr(gameId);
    setNewAgent((prev) => {
      const prevGameIds = (prev.gameIds || []).map(idStr);
      return {
        ...prev,
        gameIds: prevGameIds.includes(id)
          ? prevGameIds.filter((gid) => gid !== id)
          : [...prevGameIds, id],
      };
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!newAgent.stokez_id || !newAgent.super_stokez_id || !newAgent.admin_id || !newAgent.company_id) {
        showWarning('Please select all required fields.');
        return;
      }
      if (isEditing) {
        await dispatch(updateAgent(newAgent._id, newAgent));
        showSuccess('Agent updated');
      } else {
        await dispatch(createAgent(newAgent));
        showSuccess('Agent created');
      }
      setIsModalOpen(false);
      setNewAgent({ ...EMPTY_AGENT });
      setIsEditing(false);
      dispatch(getAgents());
    } catch (error) {
      console.error('Failed to submit agent:', error);
      showApiError(error, `Failed to ${isEditing ? 'update' : 'create'} Agent. Please try again.`);
    }
  };

  const handleEdit = useCallback(
    (agent) => {
      setIsEditing(true);
      setNewAgent({
        ...agent,
        password: '',
        commission_perc: agent.commission_perc ?? 5,
        target_perc: agent.target_perc ?? 15,
        adminTargetNTP: agent.adminTargetNTP ?? '',
      });
      setCascadingDropdownValues({
        company_id: agent.company_id,
        admin_id: agent.admin_id,
        super_stokez_id: agent.super_stokez_id,
        stokez_id: agent.stokez_id,
      });
      const filteredGames = games.filter((game) => {
        // Implement filtering logic
        return true; // Placeholder
      });
      setAvailableGames(filteredGames);
      setIsModalOpen(true);
    },
    [games]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setNewAgent({ ...EMPTY_AGENT });
    setCascadingDropdownValues({});
    setIsEditing(false);
  }, []);

  const handleToggleLock = useCallback((agentId, isLocked) => {
    dispatch(toggleLockAgent(agentId))
      .then(() => {
        dispatch(getAgents());
        showSuccess(`Agent ${isLocked ? 'unlocked' : 'locked'}.`);
      })
      .catch((error) => {
        showApiError(error, 'Failed to update lock status. Please try again.');
      });
  }, [dispatch, showSuccess, showApiError]);

  const columns = useMemo(
    () => [
      {
        Header: 'Username',
        accessor: 'username',
        defaultWidth: 120,
      },
      {
        Header: 'Password',
        accessor: 'password',
        defaultWidth: 100,
      },
      {
        Header: 'Stokez',
        accessor: 'stokez_name',
        defaultWidth: 120,
      },
      {
        Header: 'Super Stokez',
        accessor: 'super_stokez_name',
        defaultWidth: 140,
      },
      {
        Header: 'Admin',
        accessor: 'admin_name',
        defaultWidth: 120,
      },
      {
        Header: 'Company',
        accessor: 'company_name',
        defaultWidth: 150,
      },
      {
        Header: 'Balance',
        accessor: 'balance',
        defaultWidth: 100,
        Cell: ({ row }) => <Text>{row.original.balance}</Text>,
      },
      {
        Header: 'Comm %',
        accessor: 'commission_perc',
        defaultWidth: 80,
        Cell: ({ value }) => value ?? '—',
      },
      {
        Header: 'Target %',
        accessor: 'target_perc',
        defaultWidth: 80,
        Cell: ({ value }) => value ?? '—',
      },
      {
        Header: 'Admin Target NTP',
        accessor: 'adminTargetNTP',
        defaultWidth: 120,
        Cell: ({ value }) => (value != null && value !== '' ? value : '—'),
      },
      {
        Header: 'Game Codes',
        accessor: 'game_codes',
        defaultWidth: 150,
        Cell: ({ value }) => (value ? value.join(', ') : ''),
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        defaultWidth: 200,
        Cell: ({ row }) => (
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => {
                const base = slug ? `/${slug}/player` : '/player';
                const params = new URLSearchParams(location.search);
                params.set('agent', row.original.username || row.original._id);
                navigate(`${base}?${params.toString()}`);
              }}
              title="Go to Player"
              aria-label="Go to Player"
            >
              <ChevronRight size={18} />
            </button>
            {can('agent.edit') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => handleEdit(row.original)}
              title="Edit Agent"
              aria-label="Edit Agent"
            >
              <Edit size={18} />
            </button>
            )}
            {can('agent.lock') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => handleToggleLock(row.original._id, row.original.isLocked)}
              title={row.original.isLocked ? 'Unlock' : 'Lock'}
              aria-label={row.original.isLocked ? 'Unlock' : 'Lock'}
            >
              {row.original.isLocked ? <Lock size={18} /> : <Unlock size={18} />}
            </button>
            )}
            {can('agent.transfer') && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => handleOpenTransferModal('agent', row.original.username, row.original)}
              title="Transfer Balance"
              aria-label="Transfer Balance"
            >
              <DollarSign size={18} />
            </button>
            )}
          </div>
        ),
      },
    ],
    [navigate, slug, location.search, handleToggleLock, handleEdit, can]
  );

  const visibleColumns = useMemo(
    () => filterColumns('agent', columns),
    [columns, filterColumns]
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 className={styles.spinner} size={48} />
      </div>
    );
  }
  if (error && agents.length === 0) {
    return (
      <div className={styles.errorAlert}>
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.managementPage}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon} aria-hidden><Plus size={24} /></span>
          <h1 className={styles.title}>Agent Management</h1>
        </div>
        {can('agent.create') && (
        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            setIsEditing(false);
            setNewAgent({ ...EMPTY_AGENT });
            setIsModalOpen(true);
          }}
        >
          <Plus size={20} />
          <span>Add Agent</span>
        </button>
        )}
      </div>

      <div className={styles.searchBarRow}>
        <TableSearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by username, stokez, company..."
        />
      </div>

      <div className={styles.tableContainer}>
        <GenericTable columns={visibleColumns} data={filteredAgents} showColumnFilters={false} />
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                {isEditing ? 'Edit Agent' : 'Add New Agent'}
              </h2>
              <button type="button" className={styles.closeButton} onClick={handleCloseModal} aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <form className={styles.modalForm} onSubmit={handleSubmit}>
              <div className={styles.modalBody}>
                {canShowField('agent', 'username', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Username <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={newAgent.username}
                    onChange={handleInputChange}
                    className={styles.input}
                    required
                  />
                </div>
                )}
                {canShowField('agent', 'password', 'form') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Password {isEditing && <span className={styles.helpText}>(Leave blank to keep current password)</span>}
                    {!isEditing && <span className={styles.required}>*</span>}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={newAgent.password}
                    onChange={handleInputChange}
                    className={styles.input}
                    required={!isEditing}
                  />
                </div>
                )}
                {(canShowField('agent', 'stokez_id', 'form') ||
                  canShowField('agent', 'super_stokez_id', 'form') ||
                  canShowField('agent', 'admin_id', 'form') ||
                  canShowField('agent', 'company_id', 'form')) && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Stokez / Super Stokez / Admin / Company <span className={styles.required}>*</span>
                  </label>
                  <CascadingDropdown
                    onSelectionChange={handleCascadingDropdownChange}
                    initialValues={cascadingDropdownValues}
                    roleBeingCreated="agent"
                  />
                </div>
                )}
                {canShowField('agent', 'gameIds', 'form') && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Games</label>
                    {availableGames.length > 0 ? (
                      <GameSelection
                        games={availableGames}
                        selectedGames={newAgent.gameIds}
                        onGameSelection={handleGameSelection}
                        isDisabled={!newAgent.stokez_id}
                      />
                    ) : (
                      <p className={styles.helpText}>No games available for the selected Stokez</p>
                    )}
                  </div>
                )}
                {(canShowField('agent', 'commission_perc', 'form') ||
                  canShowField('agent', 'target_perc', 'form') ||
                  (isSuperAdmin && canShowField('agent', 'adminTargetNTP', 'form'))) && (
                  <>
                    <h3 className={styles.formSectionTitle}>Percentages</h3>
                    <div className={styles.formGrid}>
                      {canShowField('agent', 'commission_perc', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Commission %</label>
                          <input
                            type="number"
                            name="commission_perc"
                            step="any"
                            value={newAgent.commission_perc}
                            onChange={handleInputChange}
                            className={styles.input}
                          />
                        </div>
                      )}
                      {canShowField('agent', 'target_perc', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Target %</label>
                          <input
                            type="number"
                            name="target_perc"
                            step="any"
                            min={isSuperAdmin ? undefined : 0}
                            max={isSuperAdmin ? undefined : 100}
                            value={newAgent.target_perc}
                            onChange={handleInputChange}
                            className={styles.input}
                          />
                        </div>
                      )}
                      {isSuperAdmin && canShowField('agent', 'adminTargetNTP', 'form') && (
                        <div className={styles.formGroup}>
                          <label className={styles.label}>Admin Target NTP</label>
                          <input
                            type="number"
                            name="adminTargetNTP"
                            step="any"
                            value={newAgent.adminTargetNTP}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="Leave empty for none"
                          />
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div className={styles.modalFooter}>
                <button type="submit" className={styles.submitButton}>
                  {isEditing ? 'Update Agent' : 'Add Agent'}
                </button>
                <button type="button" className={styles.cancelButton} onClick={handleCloseModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <BalanceTransferModal
        isOpen={isTransferOpen}
        onClose={() => setIsTransferOpen(false)}
        toRole={transferDetails.toRole}
        toId={transferDetails.toId}
        recipientBalance={transferDetails.recipientBalance}
        recipientBalanceCheck={transferDetails.recipientBalanceCheck}
        onTransfer={handleBalanceTransfer}
        onTransferSuccess={(r) => applyRecipientBalancePatch(dispatch, r)}
      />
    </div>
  );
};

export default AgentManagement;
